// Custom Select — popover-based dropdown, fully controlled.
//
// Why not native <select>? Native dropdowns render in the browser's
// top layer and we cannot control their z-index or position. On
// mobile/tablet, the browser may auto-flip the menu upward to stay
// in-view, which can cover sibling elements (e.g. the search bar in
// the Inventory/Customers page header). They also can't be styled to
// match the design system.
//
// This component renders a button styled like the original Select
// (label, error, hint, chevron) and a popover via React portal. The
// portal escapes any `overflow: hidden` on ancestor .card wrappers,
// so the dropdown is never clipped. The popover is positioned with
// `position: fixed` and computed from the button's bounding rect on
// open, so it always anchors correctly regardless of the parent's
// transform/overflow stack.
//
// Behaviour parity with native <select>:
//   - Click trigger to open / click outside or Escape to close
//   - Arrow Down/Up to navigate options
//   - Enter to commit the highlighted option
//   - Home/End to jump to first/last
//   - Tab moves focus away (commit-or-cancel on blur)
//
// Backward-compatible API: callers pass <option> children exactly as
// before. We parse them at render time to extract {value, label}.

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  Children,
  isValidElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/format';

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  /** <option> children — value + text label. */
  children: ReactNode;
  label?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  /** Aria label for the trigger button when no visible label. */
  'aria-label'?: string;
  /** Placeholder when value doesn't match any option. */
  placeholder?: string;
  /** Optional id for the trigger. */
  id?: string;
}

interface ParsedOption {
  value: string;
  label: string;
}

function parseOptions(children: ReactNode): ParsedOption[] {
  const options: ParsedOption[] = [];

  // Recursive walker so we descend into <optgroup> as well — a
  // <select> in the wild often groups its options, and we want to
  // surface the same hierarchy (or at least the same options) in
  // our custom popover.
  const walk = (nodes: ReactNode) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      if (child.type === 'option') {
        const props = child.props as { value?: string; children?: ReactNode };
        const value = props.value ?? '';
        // The children of <option> can be a single string ("Active
        // only"), a number, or a React fragment/array
        // (`{evt.name} ({evt.code})` → ["Event name", " (", "CODE",
        // ")"]). The previous parser only handled the first case
        // and dropped the rest, which made the option render with
        // an empty label in the popover (and the user thought the
        // option was missing). Flatten the children to a single
        // string regardless of shape.
        const label = Children.toArray(props.children)
          .map((c) =>
            typeof c === 'string' || typeof c === 'number' ? String(c) : '',
          )
          .join('');
        options.push({ value, label });
      } else if (child.type === 'optgroup') {
        const props = child.props as { children?: ReactNode };
        walk(props.children);
      }
      // Other element types are ignored.
    });
  };

  walk(children);
  return options;
}

const POPOVER_Z = 1000;

export function Select({
  value,
  onChange,
  children,
  label,
  hint,
  error,
  disabled = false,
  className,
  placeholder = '— Pilih —',
  id,
  ...rest
}: SelectProps) {
  const reactId = useId();
  const triggerId = id || `select-${reactId}`;
  const listboxId = `${triggerId}-listbox`;

  const options = useMemo(() => parseOptions(children), [children]);
  const selected = options.find((o) => o.value === value) ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(() => {
    const idx = options.findIndex((o) => o.value === value);
    return idx >= 0 ? idx : 0;
  });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Recompute popover position when opening or on window resize
  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4, // 4px gap below the trigger
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const onResize = () => updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [isOpen, updatePosition]);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    // `click`, not `mousedown`: mousedown fires first, and unmounting the
    // panel there makes the browser dispatch the click on the common ancestor
    // of the mousedown and mouseup targets instead of the control under the
    // cursor — so the first click outside is silently eaten.
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [isOpen]);

  // Sync highlight when options / value change
  useEffect(() => {
    if (!isOpen) return;
    const idx = options.findIndex((o) => o.value === value);
    setHighlightedIndex(idx >= 0 ? idx : 0);
  }, [isOpen, value, options]);

  const handleSelect = (opt: ParsedOption) => {
    if (opt.value !== value) onChange(opt.value);
    setIsOpen(false);
    // Return focus to the trigger for keyboard users
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((i) => (i + 1) % options.length);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((i) => (i - 1 + options.length) % options.length);
        }
        break;
      case 'Home':
        if (isOpen) {
          e.preventDefault();
          setHighlightedIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          e.preventDefault();
          setHighlightedIndex(options.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        if (isOpen) {
          e.preventDefault();
          handleSelect(options[highlightedIndex]);
        } else {
          e.preventDefault();
          setIsOpen(true);
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          setIsOpen(false);
        }
        break;
      case 'Tab':
        if (isOpen) {
          // Commit-or-cancel: close on tab, no auto-select
          setIsOpen(false);
        }
        break;
    }
  };

  // Scroll the highlighted option into view (popover is virtualised
  // by the browser when items move outside its box).
  useEffect(() => {
    if (!isOpen) return;
    const el = popoverRef.current?.querySelector<HTMLElement>(
      `[data-idx="${highlightedIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, isOpen]);

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label
          htmlFor={triggerId}
          className="block text-small font-medium text-ink mb-1.5"
        >
          {label}
        </label>
      )}
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-label={rest['aria-label']}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        className={cn(
          'input w-full flex items-center justify-between text-left cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          error && 'input-error',
          !selected && 'text-neutral',
        )}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown
          size={16}
          className={cn(
            'text-ink-subtle shrink-0 transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>
      {error && (
        <p className="mt-1.5 text-caption text-danger inline-flex items-center gap-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
      {!error && hint && (
        <p className="mt-1.5 text-caption text-ink-subtle">{hint}</p>
      )}

      {isOpen && pos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            role="listbox"
            id={listboxId}
            aria-labelledby={triggerId}
            // position: fixed anchors to the viewport, not the offset
            // parent — so ancestor overflow:hidden doesn't clip us.
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: POPOVER_Z,
            }}
            className={cn(
              'bg-surface border border-line rounded-lg shadow-hover',
              'max-h-60 overflow-y-auto overscroll-contain py-1',
            )}
          >
            {options.length === 0 ? (
              <div className="px-3 py-2 text-caption text-ink-subtle">
                No options
              </div>
            ) : (
              options.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isHighlighted = idx === highlightedIndex;
                return (
                  <button
                    key={opt.value || `opt-${idx}`}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-idx={idx}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onClick={() => handleSelect(opt)}
                    className={cn(
                      'w-full text-left px-3 py-2 flex items-center gap-2 text-small',
                      'transition-colors',
                      isHighlighted
                        ? 'bg-primary/5 text-ink'
                        : 'text-ink',
                    )}
                  >
                    <span className="flex-1 truncate">{opt.label}</span>
                    {isSelected && (
                      <Check
                        size={14}
                        className="text-primary shrink-0"
                        aria-hidden
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
